//* 조문객용 부고문자 컴포넌트
import { FC, useMemo, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  bugoKakaoShareContent,
  bugoLinkUrl,
  bugoMessageContentString,
} from '@service/bugo/utils/bugoMessage.util';
import { BugoData } from '@shared/api/bugo/bugo.interface';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { ShowCashAccountType } from '@shared/api/member/member.interface';
import { Button } from '@shared/components/Button';
import { WebShareModal } from '@shared/containers/WebShareModal/WebShareModal';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { Link } from 'react-router-dom';
import { useAsync } from 'react-use';
import aplusBottom from 'src/assets/banner/aplusBottom.png';
import aplusTopLogo from 'src/assets/logo/aplusTop.png';

import { FlowerPromotionBox } from '../FlowerPromotion/FlowerPromoitonBox';
import { MemberMoney } from './MemberMoney';
import { ObituaryFeventGrid } from './ObituaryFeventGrid';
import { ObituaryFuneralHomeInfo } from './ObituaryFuneralHomeInfo';
import { ObituaryHeader } from './ObituaryHeader';
import { ObituaryMemberGrid } from './ObituaryMemberGrid';

interface Props {
  bugo: BugoData;
  forMember?: boolean;
}

const Obituary: FC<Props> = function Obituary({ bugo, forMember }: Props) {
  const openId = `bugo-${bugo._id}`;

  const setShareModalOpen = useSetRecoilState(simpleOpenAtomFamily(openId));
  const bottomButton = useMemo(() => {
    return (
      <div className="fixed bottom-0 z-20 grid h-14 w-full max-w-inherit grid-cols-2 bg-zinc-800">
        <div className="theme-bg-main">
          <Link
            className="center-box h-full w-full gap-1 text-base font-bold"
            to={`/flower-store/?bugoId=${bugo._id}`}
          >
            <span className="text-white">근조화환</span>
          </Link>
        </div>
        <Button
          className="button-rectangle h-full text-base text-white theme-bg-13"
          onClick={() => {
            setShareModalOpen(true);
          }}
        >
          <span>공유</span>
        </Button>
      </div>
    );
  }, [bugo._id, setShareModalOpen]);

  const showCashAccountType = useMemo(() => {
    if (bugo.member.showCashAccountType === ShowCashAccountType.All) {
      return true;
    }
    return false;
  }, [bugo.member.showCashAccountType]);

  const [additionalCashAccountList, setAdditionalCashAccountList] = useState<
    CashAccount[]
  >([]);

  useAsync(async () => {
    if (showCashAccountType) {
      const { data: cashAccountList } = await api_.get<CashAccount[]>(
        `public/fevent/${bugo.fevent._id}/cash-accounts`,
      );

      if (cashAccountList) {
        // Filter out the user's own cash account from the list
        const filteredCashAccountList = cashAccountList.filter(
          (account) =>
            !bugo.member.cashAccount || account._id !== bugo.member.cashAccount._id,
        );
        setAdditionalCashAccountList(filteredCashAccountList);
      }
    }
  }, [bugo.member.cashAccount, showCashAccountType]);

  const bugoBrand = bugo.bugoBrand;

  return (
    <div className="max-w-inherit pb-14">
      {/* 0th 로고 */}
      <div className="center-box h-[60px]">
        <img src={aplusTopLogo} className="h-9"></img>
      </div>
      {/* 1st header */}
      <ObituaryHeader bugo={bugo} />
      {/* 2nd 상주, 장례 정보 */}
      <div className="space-y-6 pt-6">
        <div className="grid grid-cols-auto-1fr gap-x-3 gap-y-3 whitespace-pre-line break-keep px-6 text-16 leading-6">
          <ObituaryMemberGrid bugo={bugo} />
          {/* 실제 사용하고 있는 blank div*/}
          <div></div>
          <div></div>
          <ObituaryFeventGrid bugo={bugo} />
        </div>
        {/* 3rd 장례식장 정보 */}
        <ObituaryFuneralHomeInfo bugo={bugo} />
        {/* 4th 부의금 정보 */}
        <div className="px-4">
          <MemberMoney
            cashAccount={bugo.member.cashAccount}
            additionalCashAccountList={additionalCashAccountList}
            forMember={forMember}
            cashAccountEditOpenId={`memberObituary-cashAccountOpen-${bugo._id}`}
          />
        </div>
        {/* 5th 화환 정보 */}
        <div className="px-4">
          <FlowerPromotionBox bugo={bugo} />
        </div>

        <div className="w-full">
          {bugoBrand?.bugoAgency?.homepageUrl ? (
            <a href={bugoBrand?.bugoAgency?.homepageUrl}>
              <img src={aplusBottom} className="w-full"></img>
            </a>
          ) : (
            <img src={aplusBottom} className="w-full"></img>
          )}
        </div>
      </div>
      <WebShareModal
        openId={openId}
        content={bugoMessageContentString(bugo)}
        title={'- 부고'}
        kakaoContent={bugoKakaoShareContent(bugo)}
        linkUrl={bugoLinkUrl(bugo)}
        feventId={bugo?.fevent?._id}
      />
      {bottomButton}
    </div>
  );
};

export default Obituary;
